import { styled } from "@linaria/react";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../../../../../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../../../../../../../../api/useProjects";
import type { Setup } from "../../../../../../../../../../../api/useSetups";
import HighchartsComponent from "@ui/Highcharts";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { IndexedManualFailureValues } from "../../../../../../../../../../../api/useManualFailureValues";
import type { IndexedFailureMetadata } from "../../../../../../../../../../../api/useFailureMetadata";
import useSeries from "../../../../../../../../../Diagnosis/Symptoms/useSeries";
import { filterSeriesByEventOccurrence, filterSeriesByFocus } from "../../../isFocusedFaultSeries";
import useOptions from "./useOptions";
import type { Quantity } from "../../../../../../../../../../../api/useQuantities";
import type { Event } from "../../../../../../../../../../../api/useEvents";
import type { SeriesColumnOptions } from "highcharts";
import { getEventVisibleImpactSeries } from "../../../../../../../../utils/event";

const StyledChart = styled(HighchartsComponent)`
  & .highcharts-legend-item .highcharts-point {
    display: none;
  }
`;

interface P {
  visibleSymptomHistogramChartId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  project: Project;
  failureNames: string[];
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  qoi: Quantity;
  displayAllFaults: boolean;
  event: Event;
}

const QuantityChart = ({
  visibleSymptomHistogramChartId,
  chartRef,
  project,
  failureNames,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  qoi,
  displayAllFaults,
  event,
}: P) => {
  const quantities = React.useMemo(() => [qoi], [qoi]);

  const setup = getAnalysisSetup(setups, analysis);
  const series = useSeries(
    project,
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    setup,
    quantities,
    false,
  );

  const seriesToShow = React.useMemo(() => {
    const focusedSeries = filterSeriesByFocus({
      series,
      event,
      displayAllFaults,
      failureNames,
      getEventVisibleSeries: getEventVisibleImpactSeries,
    });
    return filterSeriesByEventOccurrence({
      series: focusedSeries,
      event,
      date: analysis.date,
      displayAllFaults,
    });
  }, [series, displayAllFaults, failureNames, event, analysis.date]);
  const [visibleSeries] = useVisibleSeries(visibleSymptomHistogramChartId, seriesToShow);

  const options = useOptions({
    visibleSymptomHistogramChartId,
    qoi,
    series: visibleSeries as SeriesColumnOptions[],
    analysis,
    setups,
    project,
    flagSelectedQuantities: true,
  });

  return (
    <StyledChart
      name={`Diagnosis QOI ${qoi.name}`}
      ref={chartRef}
      options={options}
      selectablePoints={false}
    />
  );
};

export default React.memo(QuantityChart);
